import React from 'react';
import { TaskStage } from '../../../types/Task';

interface StatusBarProps {
  currentStatus: TaskStage;
}

export const TASK_STATUSES = [
  { value: TaskStage.PROPOSED, label: 'Proposed' },
  { value: TaskStage.PLANNED, label: 'Planned' },
  { value: TaskStage.ACTIVE, label: 'Active' },
  { value: TaskStage.ACTIVE_MARKED_AS_COMPLETE, label: 'Marked as Complete' },
  { value: TaskStage.INTERNAL_REVIEW, label: 'Waiting for Internal Review' },
  { value: TaskStage.CLOSED, label: 'Closed' },
  { value: TaskStage.FINALIZED, label: 'Finalized' }
] as const;

const CIRCLE_DIAMETER = 30; // px
const STATUS_CONTAINER_WIDTH = 90; // px

const StatusBar: React.FC<StatusBarProps> = ({ currentStatus }) => {
  // Find the index of the current status
  const currentIndex = TASK_STATUSES.findIndex(status => status.value === currentStatus);
  const totalStatuses = TASK_STATUSES.length;

  return (
    // The 7 steps need 7 × 90px = 630px, which cannot fit a phone. Scroll the stepper in its
    // own viewport so it never forces the whole modal body to scroll sideways; on desktop the
    // inner track just fills the width as before, so nothing changes there.
    <div className="tw-mb-4 tw-overflow-x-auto tw-overscroll-x-contain">
    <div className="tw-relative" style={{ width: '100%', minWidth: `${STATUS_CONTAINER_WIDTH * totalStatuses}px` }}>
      {/* Progress line background */}
      <div
        className="tw-flex tw-items-center"
        style={{ position: 'absolute', top: '15px', left: `${STATUS_CONTAINER_WIDTH/2}px`, right: `${STATUS_CONTAINER_WIDTH/2}px`, height: '2px', zIndex: 1 }}
      >
        <div className="tw-bg-line" style={{ width: '100%', height: '2px' }} />
      </div>
      {/* Progress line foreground (green) */}
      <div
        className="tw-flex tw-items-center"
        style={{
          position: 'absolute',
          top: '15px',
          left: `${STATUS_CONTAINER_WIDTH/2}px`,
          width: `calc((100% - ${STATUS_CONTAINER_WIDTH}px) * ${currentIndex} / ${totalStatuses - 1})`,
          height: '2px',
          zIndex: 2,
          transition: 'width 0.3s ease-in-out'
        }}
      >
        <div className="tw-bg-success" style={{ width: '100%', height: '2px' }} />
      </div>
      {/* Status circles */}
      <div className="tw-flex tw-justify-between tw-items-center" style={{ position: 'relative', zIndex: 3 }}>
        {TASK_STATUSES.map((status, index) => {
          const isCompleted = index <= currentIndex;
          const isCurrent = status.value === currentStatus;
          return (
            <div
              key={status.value}
              className="tw-flex tw-flex-col tw-items-center"
              style={{ minWidth: `${STATUS_CONTAINER_WIDTH}px`, maxWidth: `${STATUS_CONTAINER_WIDTH}px`, flex: `0 1 ${STATUS_CONTAINER_WIDTH}px` }}
            >
              <div
                className={`tw-rounded-full tw-flex tw-items-center tw-justify-center ${isCompleted ? 'tw-bg-success' : 'tw-bg-line'}`}
                style={{
                  width: `${CIRCLE_DIAMETER}px`,
                  height: `${CIRCLE_DIAMETER}px`,
                  color: 'white',
                  marginBottom: '8px',
                  transition: 'background-color 0.3s ease-in-out',
                  flexShrink: 0
                }}
              >
                {isCompleted && <i className="bi bi-check2"></i>}
              </div>
              <span
                className={`tw-text-sm ${isCurrent ? 'tw-text-success tw-font-semibold' : 'tw-text-content-muted'}`}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  fontSize: '12px',
                  lineHeight: 1.1,
                  height: '28px', // enough for two lines
                  marginTop: '2px',
                  overflow: 'hidden'
                }}
              >
                {status.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
};

export default StatusBar;